// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceProfile } from '../api/intelligenceApi';
import { pickVoice } from '../lib/pickVoice';
import { prepareTextForSpeech } from '../lib/prepareTextForSpeech';

/**
 * Browser-only voice IO. Speech synthesis (output) works in most browsers;
 * speech recognition (input) is reliable in Chrome/Edge/Safari but limited
 * in Firefox. The hook exposes capability flags so the UI can hide controls
 * cleanly.
 *
 * The hook deliberately holds no permanent listener references — every
 * recognition session creates a fresh instance to avoid the stuck-recognizer
 * issue some browsers exhibit on long-lived instances.
 */
export interface UseVoiceChatOptions {
  enabled: boolean;
  profile?: VoiceProfile;
  onTranscript?: (text: string) => void;
}

export interface UseVoiceChatResult {
  speak: (text: string) => void;
  cancelSpeak: () => void;
  startRecording: () => void;
  stopRecording: () => void;
  isRecording: boolean;
  isSpeaking: boolean;
  recognitionSupported: boolean;
  synthesisSupported: boolean;
  voices: SpeechSynthesisVoice[];
}

declare global {
  interface Window {
    webkitSpeechRecognition?: typeof SpeechRecognition;
    SpeechRecognition?: typeof SpeechRecognition;
  }
}

export function useVoiceChat(opts: UseVoiceChatOptions): UseVoiceChatResult {
  const { enabled, profile, onTranscript } = opts;

  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const synthesisSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const recognitionSupported = typeof window !== 'undefined'
    && (typeof window.SpeechRecognition !== 'undefined'
        || typeof window.webkitSpeechRecognition !== 'undefined');

  useEffect(() => {
    if (!synthesisSupported) return;
    const handler = () => setVoices(window.speechSynthesis.getVoices());
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    handler();
    return () => window.speechSynthesis.removeEventListener('voiceschanged', handler);
  }, [synthesisSupported]);

  // Cancel any in-flight speech / recognition on unmount
  useEffect(() => {
    return () => {
      if (synthesisSupported) window.speechSynthesis.cancel();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, [synthesisSupported]);

  const speak = useCallback((text: string) => {
    if (!enabled || !synthesisSupported || !text) return;
    const cleaned = prepareTextForSpeech(text);
    if (!cleaned) return;
    const u = new SpeechSynthesisUtterance(cleaned);
    u.lang = profile?.locale ?? 'en-US';
    u.rate = profile?.rate ?? 1.0;
    u.pitch = profile?.pitch ?? 1.0;
    const voice = pickVoice(voices, profile ?? {});
    if (voice) u.voice = voice;
    u.onstart = () => setIsSpeaking(true);
    u.onend = () => setIsSpeaking(false);
    u.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }, [enabled, synthesisSupported, profile, voices]);

  const cancelSpeak = useCallback(() => {
    if (!synthesisSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [synthesisSupported]);

  const startRecording = useCallback(() => {
    if (!enabled || !recognitionSupported || isRecording) return;
    const Ctor = (window.SpeechRecognition ?? window.webkitSpeechRecognition);
    if (!Ctor) return;
    const recognition = new Ctor();
    recognition.lang = profile?.locale ?? 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript && onTranscript) onTranscript(transcript.trim());
    };
    recognitionRef.current = recognition;
    recognition.start();
  }, [enabled, recognitionSupported, isRecording, profile, onTranscript]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
  }, []);

  return {
    speak, cancelSpeak,
    startRecording, stopRecording,
    isRecording, isSpeaking,
    recognitionSupported, synthesisSupported,
    voices,
  };
}
